import {getCurrentLanguage} from '@/i18n'
import {sessionId, getCurrentActionId, buildTraceparent} from '@/observability/session'

// Use relative URLs - Vite dev server proxies to backend
const API_BASE = ''

/* eslint-disable i18next/no-literal-string */
function headers(): Record<string, string> {
  const actionId = getCurrentActionId()
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept-Language': getCurrentLanguage(),
    'X-Correlation-ID': crypto.randomUUID(),
    'X-Session-Id': sessionId(),
    traceparent: buildTraceparent(),
  }
  if (actionId !== null) {
    h['X-Action-Id'] = actionId
  }
  return h
}
/* eslint-enable i18next/no-literal-string */

class ApiError extends Error {
  requestUrl?: string
  errorDetail?: string
  requestHeaders?: Record<string, string>
  responseHeaders?: Record<string, string>
  params: Record<string, unknown>
  /**
   * Backend-side error log id, populated by the GlobalExceptionHandler in the
   * error response. When present, the frontend can link directly to that entry
   * (which carries the real Java stack) instead of creating a duplicate
   * FrontendApiError record.
   */
  backendErrorLogId?: string

  constructor(
    public status: number,
    public code: string,
    messageOrBody?: string | unknown,
    public field?: string,
    public pointer?: string,
    params?: Record<string, unknown>,
  ) {
    const message = typeof messageOrBody === 'string' ? messageOrBody : ''
    super(message)
    // eslint-disable-next-line i18next/no-literal-string
    this.name = 'ApiError'
    this.params = params ?? {}
    if (messageOrBody !== null && typeof messageOrBody === 'object') {
      this.errorDetail = JSON.stringify(messageOrBody)
    }
  }
}

async function handleResponse<T>(response: Response, reqHeaders?: Record<string, string>): Promise<T> {
  if (!response.ok) {
    /* eslint-disable i18next/no-literal-string */
    const body = await response.json().catch(() => null)
    const resHeaders = extractHeaders(response.headers)
    const detail = body ? JSON.stringify(body) : undefined
    const firstError = body?.errors?.[0]
    // WHY: 401 always means the user's session is gone — show
    // "session-expired", never "access denied". 502/503/504 happen when
    // the backend is restarting or behind a tripped circuit breaker —
    // surface "server-unavailable" so the user retries instead of
    // wondering what permission they lost. The backend's GN0003
    // (legitimate access-denied with a valid session) keeps its current
    // toast.
    let code = firstError?.code || body?.code
    if (!code) {
      if (response.status === 401) code = 'SESSION_EXPIRED'
      else if (response.status === 502 || response.status === 503 || response.status === 504) {
        code = 'SERVER_UNAVAILABLE'
      } else code = 'UNKNOWN_ERROR'
    }
    const message = firstError?.detail || body?.message || body?.detail
        || (response.status === 401 ? 'Your session has expired — please log in again.'
            : response.status === 502 || response.status === 503 || response.status === 504
                ? 'Server is unavailable — please retry in a moment.'
                : response.statusText)
    const pointer = firstError?.source?.pointer || undefined
    const field = pointer?.replace(/^\//, '') || undefined
    const params = firstError?.params ?? {}
    const err = new ApiError(response.status, code, message, field, pointer, params)
    err.requestUrl = response.url
    err.errorDetail = detail
    err.requestHeaders = reqHeaders
    err.responseHeaders = resHeaders
    err.backendErrorLogId = firstError?.errorLogId || undefined
    throw err
    /* eslint-enable i18next/no-literal-string */
  }

  // Handle empty responses
  const text = await response.text()
  if (!text) {
    return undefined as T
  }

  return JSON.parse(text) as T
}

// WHY: when the backend isn't reachable at all (Vite proxy can't connect,
// browser is offline, restart in progress) `fetch()` throws TypeError
// before we ever see a Response. Wrap every transport in this guard so the
// surfaced error carries our `SERVER_UNAVAILABLE` code instead of the
// generic `Failed to fetch` string that the toast layer can't translate.
async function fetchOrThrowNetwork(input: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init)
  } catch (cause) {
    const err = new ApiError(0, 'SERVER_UNAVAILABLE', 'Server is unavailable — please retry in a moment.')
    err.requestUrl = input
    err.errorDetail = cause instanceof Error ? cause.message : String(cause)
    throw err
  }
}

function extractHeaders(h: Headers): Record<string, string> {
  const result: Record<string, string> = {}
  h.forEach((value, key) => { result[key] = value })
  return result
}

export const api = {
  async get<T>(url: string): Promise<T> {
    const h = headers()
    const response = await fetchOrThrowNetwork(`${API_BASE}${url}`, {
      credentials: 'include',
      headers: h,
    })
    return handleResponse<T>(response, h)
  },

  async post<T>(url: string, data?: unknown): Promise<T> {
    const h = headers()
    const response = await fetchOrThrowNetwork(`${API_BASE}${url}`, {
      method: 'POST',
      credentials: 'include',
      headers: h,
      body: data ? JSON.stringify(data) : undefined,
    })
    return handleResponse<T>(response, h)
  },

  async put<T>(url: string, data?: unknown): Promise<T> {
    const h = headers()
    const response = await fetchOrThrowNetwork(`${API_BASE}${url}`, {
      method: 'PUT',
      credentials: 'include',
      headers: h,
      body: data ? JSON.stringify(data) : undefined,
    })
    return handleResponse<T>(response, h)
  },

  async delete<T>(url: string, data?: unknown): Promise<T> {
    const h = headers()
    const response = await fetchOrThrowNetwork(`${API_BASE}${url}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: h,
      body: data ? JSON.stringify(data) : undefined,
    })
    return handleResponse<T>(response, h)
  },
}

export { ApiError }
